import {
  getAdminV1StoresStoreIdReportsAuctions,
  getAdminV1StoresStoreIdReportsCustomers,
  getAdminV1StoresStoreIdReportsExport,
  getAdminV1StoresStoreIdReportsInventory,
  getAdminV1StoresStoreIdReportsOverview,
  getAdminV1StoresStoreIdReportsPayments,
  getAdminV1StoresStoreIdReportsProducts,
  getAdminV1StoresStoreIdReportsProductsEngagement,
  getAdminV1StoresStoreIdReportsSales,
  getSupportReport,
  postAdminV1StoresStoreIdReportsRollup,
  type AuctionReport,
  type CustomersReport,
  type GetAdminV1StoresStoreIdReportsExportParams,
  type GetAdminV1StoresStoreIdReportsProductsEngagementSort,
  type InventoryReport,
  type OverviewReport,
  type PaymentsReport,
  type PostAdminV1StoresStoreIdReportsRollup200,
  type ProductsReport,
  type ReportInterval,
  type SalesReport,
  type StoreProductEngagementReport,
  type SupportReport,
} from '@nucleus-commerce/api-client/admin';

import type { DayRange } from '../schemas/report-range';

/** Store reports read from the daily rollups; every range is inclusive store-timezone days. */
export const reportsApi = {
  overview: (storeId: string, range: DayRange): Promise<OverviewReport> =>
    getAdminV1StoresStoreIdReportsOverview({ storeId }, range),
  sales: (storeId: string, range: DayRange, interval: ReportInterval): Promise<SalesReport> =>
    getAdminV1StoresStoreIdReportsSales({ storeId }, { ...range, interval }),
  products: (storeId: string, range: DayRange, limit: number): Promise<ProductsReport> =>
    getAdminV1StoresStoreIdReportsProducts({ storeId }, { ...range, limit }),
  /** Views, carts, wishlist adds and sales per product from the daily product rollup. */
  productEngagement: (
    storeId: string,
    range: DayRange,
    sort: GetAdminV1StoresStoreIdReportsProductsEngagementSort,
    limit: number,
  ): Promise<StoreProductEngagementReport> =>
    getAdminV1StoresStoreIdReportsProductsEngagement({ storeId }, { ...range, sort, limit }),
  customers: (storeId: string, range: DayRange, limit: number): Promise<CustomersReport> =>
    getAdminV1StoresStoreIdReportsCustomers({ storeId }, { ...range, limit }),
  payments: (storeId: string, range: DayRange): Promise<PaymentsReport> =>
    getAdminV1StoresStoreIdReportsPayments({ storeId }, range),
  /** Totals over the whole range on every page; the closed auctions a page at a time. */
  auctions: (storeId: string, range: DayRange, cursor?: string): Promise<AuctionReport> =>
    getAdminV1StoresStoreIdReportsAuctions(
      { storeId },
      { ...range, limit: 50, ...(cursor ? { cursor } : {}) },
    ),
  support: (storeId: string, range: DayRange): Promise<SupportReport> =>
    getSupportReport({ storeId }, range),
  inventory: (storeId: string): Promise<InventoryReport> =>
    getAdminV1StoresStoreIdReportsInventory({ storeId }),
  rollup: (storeId: string, range: DayRange): Promise<PostAdminV1StoresStoreIdReportsRollup200> =>
    postAdminV1StoresStoreIdReportsRollup({ storeId }, range),
  /** The CSV text of one report; products and customers carry their top 1000. */
  exportCsv: (
    storeId: string,
    params: GetAdminV1StoresStoreIdReportsExportParams,
  ): Promise<string> =>
    getAdminV1StoresStoreIdReportsExport({ storeId }, params, { responseType: 'text' }),
};
